/**
 * fusion-admin/router.js — the Fusion Meals admin's few pages: the console
 * itself (views/meals/MealsHome.vue, the same as in the Pizza Felice app) and
 * the login pages it needs.
 *
 * Who gets in is what the meals API itself allows (backend/meals/routes:
 * requireAdmin + requireAppAccess("meals")): an admin with the "meals" grant,
 * or an appadmin. Grants are given in the Pizza Felice app, Settings → Users.
 */
import { createRouter, createWebHistory } from "vue-router";
import { isAuthenticated } from "@/utils/api.js";

const routes = [
  { path: "/", name: "MealsHome", component: () => import("@/views/meals/MealsHome.vue"), meta: { requiresAuth: true } },
  // LoginForm goes to /home after signing in, as in the Pizza Felice app
  { path: "/home", redirect: "/" },
  { path: "/meals", redirect: "/" },
  { path: "/login", name: "Login", component: () => import("./FusionLogin.vue"), meta: { public: true } },
  { path: "/forgot-password", name: "ForgotPassword", component: () => import("@/views/auth/ForgotPassword.vue"), meta: { public: true } },
  { path: "/force-password-change", name: "ForcePasswordChange", component: () => import("@/views/auth/ForcePasswordChange.vue") },
  { path: "/no-access", name: "NoAccess", component: () => import("./NoAccess.vue"), meta: { public: true } },
  { path: "/:catchAll(.*)*", redirect: "/" },
];

const router = createRouter({ history: createWebHistory(), routes });

/** The signed-in user as LoginForm stored it. */
function storedUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

/** Same rule as the meals API: admin or appadmin, and appadmin or the "meals" grant. */
export function canUseMeals(user = storedUser()) {
  if (!user) return false;
  if (user.role === "appadmin") return true;
  if (user.role !== "admin") return false;
  const apps = Array.isArray(user.allowed_apps) ? user.allowed_apps : JSON.parse(user.allowed_apps || "[]");
  return apps.includes("meals");
}

router.beforeEach((to) => {
  if (to.meta.public) {
    // Already signed in: the login page has nothing to offer
    if (to.name === "Login" && isAuthenticated()) return { path: "/" };
    return true;
  }
  if (!isAuthenticated()) {
    if (to.fullPath !== "/") localStorage.setItem("lastPath", to.fullPath);
    return { name: "Login" };
  }
  if (localStorage.getItem("passwordChangeRequired") === "true" && to.name !== "ForcePasswordChange") {
    return { name: "ForcePasswordChange" };
  }
  if (to.name !== "ForcePasswordChange" && !canUseMeals()) return { name: "NoAccess" };
  return true;
});

export default router;
